import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DemiLightText, MediumText } from '../../lib/Text';
import { Col, getItemWithExpiration, Row } from '../../lib/utils';
import useSingleQuery from '../../hook/useSingleQuery';
import {
    FeedDashboardDetailResponse,
    fetchGetFeedDashboardCoachDetail,
    LatestSessionReview,
} from '../../services/useFeedDashboardQueryService';
import dayjs from 'dayjs';
import NewProfileImage from '../../components/NewProfileImage';
import { colors } from '../../lib/colors';
import SvgIcon from '../../components/SvgIcon';
import FeedCoachStatisticsPageCoachAverageStats from './FeedCoachStatisticsPageCoachAverageStats';
import FeedCoachStatisticsPageCoachReportWriteRateGraph from './FeedCoachStatisticsPageCoachReportWriteRateGraph';
import ModalFeedCoachStatisticsPageAllMember from './ModalFeedCoachStatisticsPageAllMember';
import ModalFeedCoachStatisticsPageMemberWritingRate from './ModalFeedCoachStatisticsPageMemberWritingRate';
import { useRecoilState } from 'recoil';
import { recoilCenterReportMonth } from '@/atoms/atom';
import CoachPerformanceSummary from './CoachPerformanceSummary';

function FeedCoachStatisticsPage() {
    const { coachId: _coachId } = useParams<{ coachId: string }>();
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const [authorization] = useState(getItemWithExpiration('authorization'));

    const [month, setMonth] = useRecoilState(recoilCenterReportMonth);
    const startDate = dayjs(month).startOf('month').format('YYYY.MM.DD');
    // 이번달일경우 오늘이 endDate
    const endDate = dayjs(month).isSame(dayjs(), 'month')
        ? dayjs().format('YYYY.MM.DD')
        : dayjs(month).endOf('month').format('YYYY.MM.DD');
    const { apiStartDate, apiEndDate } = useMemo(() => {
        const start = dayjs(month).startOf('month').toISOString();
        const end = dayjs(month).isSame(dayjs(), 'month')
            ? dayjs().toISOString()
            : dayjs(month).endOf('month').toISOString();
        return {
            apiStartDate: start,
            apiEndDate: end,
        };
    }, [month]);
    const [sessionReview] = useState<LatestSessionReview>();

    // 수업리뷰 모달 여부
    const [isSessionReviewModalOpen, setIsSessionReviewModalOpen] = useState(false);
    // 작성률 모달 여부
    const [isMemberWritingRateModalOpen, setIsMemberWritingRateModalOpen] = useState(false);

    const { refinedResult } = useSingleQuery({
        fetchFn: fetchGetFeedDashboardCoachDetail,
        params: { centerId: authObject.centerId, coachId: _coachId, startDate: apiStartDate, endDate: apiEndDate },
        enabled: !!authorization,
        suspense: false,
    });
    const data = refinedResult?.data as FeedDashboardDetailResponse;

    const coachId = _coachId ?? '';
    const coachInfo = data?.coachInfo;
    const coachName = coachInfo?.name;
    const coachProfileImage = coachInfo?.profileImageUrl;

    const coachAverageStats = data?.coachAverageStats;
    const coachReportWriteRateList = data?.coachReportWriteRateList;
    const coachStatChangesLast3Months = data?.coachStatChangesLast3Months;

    const onNavigate = (direction: 'PREV' | 'NEXT') => {
        const currentMonth = dayjs(month);
        if (direction === 'PREV') {
            setMonth(currentMonth.subtract(1, 'month').toISOString());
        } else if (direction === 'NEXT') {
            // 미래달 안되게
            if (currentMonth.isSameOrAfter(dayjs(), 'month')) {
                return;
            }
            setMonth(currentMonth.add(1, 'month').toISOString());
        }
    };
    return (
        <div className="flex flex-col w-full  items-center bg-[#F4F5F7]">
            <Col className="w-full min-w-0 max-w-content-clamp pb-[100px]">
                <Row className="mb-[36px] flex w-full flex-wrap items-center gap-y-[12px]">
                    <NewProfileImage
                        imageUrl={coachProfileImage}
                        size={44}
                        iconSize={20}
                        imageFill="white"
                        className="mr-[9px]"
                        circleColor="#E7EAF0"
                    />
                    <MediumText fontSize={25} lineHeight={30} color={colors.Text_1}>
                        {coachName ? `${coachName} 강사 성과 리포트` : '강사 성과 리포트'}
                    </MediumText>
                    <Col className="flex w-[0.5px] h-[32px] bg-[#3D4244] mx-[20px]" />
                    <MediumText fontSize={22} lineHeight={27} color={colors.Text_1} className="mr-[11px]">
                        {dayjs(month).format('YYYY년 MM월')}
                    </MediumText>
                    <div
                        className="flex cursor-pointer bg-white w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[5px] border border-solid border-[#E0E0E0]"
                        onClick={() => onNavigate('PREV')}
                    >
                        <SvgIcon
                            name={'SvgLeftStrokeArrow'}
                            size={15}
                            fill={colors.trans}
                            stroke={'#666B73'}
                            strokeWidth={1.75}
                        />
                    </div>

                    <div
                        className={`flex cursor-pointer bg-white w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[12px] border border-solid border-[#E0E0E0] 
                                 ${dayjs(month).isSameOrAfter(dayjs(), 'month') ? ' opacity-30' : ' '}`}
                        onClick={() => onNavigate('NEXT')}
                    >
                        <SvgIcon
                            name={'SvgRightStrokeArrow'}
                            size={15}
                            fill={colors.trans}
                            stroke={'#666B73'}
                            strokeWidth={1.75}
                        />
                    </div>
                    <DemiLightText fontSize={16} lineHeight={21} color={colors.Text_1} className="mr-[40px]">
                        {startDate} - {endDate}
                    </DemiLightText>
                </Row>
                <section aria-labelledby="coach-performance-heading" className="mb-[48px] min-w-0">
                    <div className="mb-[16px] flex flex-wrap items-end justify-between gap-[8px]">
                        <div>
                            <h2
                                id="coach-performance-heading"
                                className="text-[22px] font-semibold leading-[30px] text-[#1A305D]"
                            >
                                월간 성과 요약
                            </h2>
                            <p className="mt-[3px] text-[13px] leading-[19px] text-[#8896B0]">
                                카드를 누르면 지표별 원천 근거나 상세 화면으로 이동합니다. 집계 대상이 없는 지표는
                                이동할 수 없습니다.
                            </p>
                        </div>
                    </div>
                    <CoachPerformanceSummary
                        centerId={authObject?.centerId ?? ''}
                        coachId={coachId}
                        month={month}
                    />
                </section>
                <section aria-labelledby="coach-detail-heading" className="min-w-0">
                    <div className="mb-[16px]">
                        <h2
                            id="coach-detail-heading"
                            className="text-[22px] font-semibold leading-[30px] text-[#1A305D]"
                        >
                            일지·재등록 성과 상세
                        </h2>
                        <p className="mt-[3px] text-[13px] leading-[19px] text-[#8896B0]">
                            일지 작성률, 재등록 회원 비중, 정상 종료 후 재등록 성공률을 자세히 확인합니다.
                        </p>
                    </div>
                    <Row className="flex w-full h-full justify-between">
                        <Col className="flex w-full h-full">
                            {/* 첫번째줄 강사 통계 */}
                            <FeedCoachStatisticsPageCoachAverageStats coachAverageStats={coachAverageStats} />
                            {/* 두번째줄 그래프 */}
                            <FeedCoachStatisticsPageCoachReportWriteRateGraph
                                coachStatChangesLast3Months={coachStatChangesLast3Months}
                                setIsMemberWritingRateModalOpen={setIsMemberWritingRateModalOpen}
                                coachReportWriteRateList={coachReportWriteRateList}
                            />
                        </Col>
                    </Row>
                </section>
            </Col>
            {isSessionReviewModalOpen && (
                <ModalFeedCoachStatisticsPageAllMember
                    setIsVisible={setIsSessionReviewModalOpen}
                    sessionReview={sessionReview}
                />
            )}
            {isMemberWritingRateModalOpen && (
                <ModalFeedCoachStatisticsPageMemberWritingRate
                    setIsVisible={setIsMemberWritingRateModalOpen}
                    coachReportWriteRateList={coachReportWriteRateList}
                    month={dayjs(month).month() + 1}
                    coachId={coachId}
                />
            )}
        </div>
    );
}

export default FeedCoachStatisticsPage;
